import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import debounce from 'lodash.debounce';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import SearchBarEvents from '~/constants/srp/SearchBarEvents';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import type { Suggestion } from '~/contracts/srp';
import {
  fetchSuggestionsApi,
  getFromLocalStorage,
  removeSuggestion,
  saveSuggestion,
  saveToLocalStorage
} from '~/services/srp/suggestionService';
import isServerSideRendering from '~/utilities/isServerSideRendering';

type SuggestionContextType = {
  addSavedSuggestion: (_value: Suggestion) => void;
  clearSavedSuggestions: () => void;
  clearSuggestions: () => void;
  debouncedFetchSuggestions: (
    _value: string,
    _controller: AbortController
  ) => void;
  removeSavedSuggestion: (_value: Suggestion) => void;
  suggestions: LoadableState<Suggestion[]>;
  savedSuggestions: Suggestion[];
};

const defaultContext: SuggestionContextType = {
  addSavedSuggestion: _value => {},
  clearSavedSuggestions: () => {},
  clearSuggestions: () => {},
  debouncedFetchSuggestions: (_value, _controller) => {},
  removeSavedSuggestion: _value => {},
  suggestions: defaultLoadableState<Suggestion[]>([]),
  savedSuggestions: []
};

const SuggestionsContext = createContext<SuggestionContextType>(defaultContext);

export function SuggestionsContextProvider({ children }) {
  const suggestions = useLoadableState<Suggestion[]>([]);
  const [savedSuggestions, setSavedSuggestions] = useState<Suggestion[]>(
    isServerSideRendering ? [] : getFromLocalStorage()
  );
  const trackEvent = useTrackEvent();

  const addSavedSuggestion = useCallback(suggestion => {
    setSavedSuggestions(saveSuggestion(suggestion));
  }, []);

  const clearSavedSuggestions = useCallback(() => {
    setSavedSuggestions([]);
  }, []);

  const clearSuggestions = useCallback(() => {
    suggestions.setData([]);
  }, [suggestions]);

  const fetchSuggestions = useCallback(
    async (value, controller) => {
      const query = value.trim();

      if (!query) return;
      const data = await fetchSuggestionsApi(query, controller);

      if (!data) return;
      suggestions.setData(data);

      if (data.length === 0) {
        trackEvent(SearchBarEvents.NoResults, { textEntered: query });
      }
    },
    [suggestions, trackEvent]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSuggestions = useCallback(
    debounce(fetchSuggestions, 250),
    []
  );

  const removeSavedSuggestion = useCallback((suggestion: Suggestion) => {
    const newSuggestionsArray = removeSuggestion(suggestion);
    setSavedSuggestions(newSuggestionsArray);
  }, []);

  useEffect(() => {
    saveToLocalStorage(savedSuggestions);
  }, [savedSuggestions]);

  const value = useMemo<SuggestionContextType>(
    () => ({
      addSavedSuggestion,
      clearSavedSuggestions,
      clearSuggestions,
      debouncedFetchSuggestions,
      removeSavedSuggestion,
      suggestions,
      savedSuggestions
    }),
    [
      addSavedSuggestion,
      clearSavedSuggestions,
      clearSuggestions,
      debouncedFetchSuggestions,
      removeSavedSuggestion,
      suggestions,
      savedSuggestions
    ]
  );

  return (
    <SuggestionsContext.Provider value={value}>
      {children}
    </SuggestionsContext.Provider>
  );
}

SuggestionsContextProvider.displayName = 'SuggestionsContextProvider';

export default SuggestionsContext;
